import { useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useLazyGetAuthenticatedUserQuery, useLazyGetConfigQuery, useLazyGetCsrfTokenQuery, useLoginMutation } from "src/api";
import { routes } from "src/app/routes";
import { navigateExternal, useComponentDidMount } from "src/utils";

export const useAuth = () => {
    const { href } = window.location;
    const redirectUri = href.split("?").shift();

    const loading = useRef(true);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [getCsrfToken] = useLazyGetCsrfTokenQuery();
    const [login] = useLoginMutation();
    const [getAuthedUser, authedUserResult] = useLazyGetAuthenticatedUserQuery();
    const [getConfig] = useLazyGetConfigQuery();

    const hasCode = searchParams.has("code");

    const tryLogin = useCallback(async () => {
        try {
            const code = searchParams.get("code")!;

            // Clear all OIDC callback params (code, session_state, iss, ...) from the URL/history
            // immediately, via replace (not push), before any same-origin request is made - fixes
            // pentest finding 2.3.1 (the code otherwise persists in browser history and leaks via
            // the Referer header on the requests below).
            setSearchParams(new URLSearchParams(), { replace: true });

            const tokenResponse = await getCsrfToken().unwrap();
            await login({ code, redirectUri: redirectUri!, csrfToken: tokenResponse.token! }).unwrap();

            await getAuthedUser().unwrap();
        } catch (e) {
            if ((e as Response).status === 403) {
                loading.current = false;
                navigate(routes.accessDenied.path);
            }
        }
    }, [getAuthedUser, getCsrfToken, login, navigate, redirectUri, searchParams, setSearchParams]);

    const redirectToKeycloak = useCallback(async () => {
        try {
            await getAuthedUser().unwrap();
        } catch (e) {
            const config = await getConfig().unwrap();
            const { keycloak } = config;
            const { authUri, clientId } = keycloak;

            navigateExternal(authUri, {
                client_id: clientId,
                redirect_uri: redirectUri,
                response_type: "code",
                scope: "openid",
                enable_kerberos: true,
            });
        }
    }, [getAuthedUser, getConfig, redirectUri]);

    useComponentDidMount(() => {
        if (hasCode) {
            tryLogin();
        } else {
            redirectToKeycloak();
        }
    });

    const { status, isError } = authedUserResult;
    if (status === "fulfilled" && !isError) {
        loading.current = false;
    }

    return { loading: loading.current };
};
